import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  SetStateAction,
  Dispatch,
} from 'react';
import { useDispatch } from 'react-redux';
import { Control, DeepPartial, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { compressForUpload } from '../../../utils/imageCompression';

// Components Styled
import { Title } from '../components/MainStyled';
import {
  ContainerRow,
  HiddenInput,
  ContainerAttention,
  ContainerInputLoads,
  SketchImagesContainer,
  ImagesContainer,
  SketchContainer,
  InlineLoaderSlot,
} from './styles';

//Components
import { AttentionAccidentTable } from '../../display/tables/formTables/AttentionAccidentTable';
import { MainButton } from '../../inputs/buttons/MainButton';
import { InputLoadImages } from '../../inputs/InputLoadImages';
import { Typography } from '../../display/Typography';
import { Loader } from '../../inputs/Loader';

// Redux
import { closeModal, openModal } from '../../../state/actions/ui/modal';
import { modalIds } from '../../modals/SuperModal/types';

// Types
import { type Accident } from '../../../types/accident';
import { type Sketch, type Img, type SetValueType, CustomFile } from './types';
import { ButtonIconBackground } from '../../inputs/buttons/ButtonIconBackground';
import { InputLoadFiles } from '../../inputs/InputLoadFiles';
import { type AttentioAccidentComunication } from '../../display/tables/formTables/AttentionAccidentTable/types';
import { OrderType } from '../../../types/orders';

// Constants
import { formAccidentNaming, directoryNaming } from './constants';

// Files
import {
  deleteFilesFromDirectory,
  getFilesFromDirectory,
  listFilesWithDates,
  saveFilesIntoADirectory,
  dataURItoBlob,
  generateFileName,
} from '../../../utils/files';
import { makeUniqueFileName, wasPickedAs } from '../../../utils/uniqueFileName';
import {
  restoreSketchToSessionStorage,
  clearSketchFromSessionStorage,
} from '../../../utils/sketch';
import {
  removeDraftSketchFile,
  saveDraftSketchFile,
  sketchFileNameMarker,
} from '../../../utils/sketchFile';
import { screenImageSource } from '../../../offline/formStorage';
import Debug from 'debug';

const log = Debug('rois:sketch:accident');

const enableSketchDebug = () => Debug.enable('rois:sketch:*');

export const AccidentAttention = ({
  control,
  setValue,
  errors,
  defaultValues,
  accidentDate,
  setIsLoadingFiles,
  isLoadingFiles,
}: {
  control: Control<Accident>;
  errors: FieldErrors<Accident>;
  defaultValues: Readonly<DeepPartial<Accident>> | undefined;
  accidentDate: string;
  setIsLoadingFiles: Dispatch<SetStateAction<boolean>>;
  isLoadingFiles: boolean;
} & SetValueType) => {
  const { t } = useTranslation('accidents');
  const dispatch = useDispatch();

  // References
  const inputRef = useRef<HTMLInputElement>(null);

  // States
  const [selectedImages, setSelectedImages] = useState<Img[]>([]);
  const [sketch, setSketch] = useState<Sketch | null>(null);

  const [selectedFiles, setSelectedFiles] = useState<CustomFile[]>([]);

  const getAccidentTableValues = () => {
    const keys: (keyof AttentioAccidentComunication)[] = [
      'noticeConservationCenterDate',
      'arrivalConservationCenterDate',
      'departureConservationCenterDate',
      'noticeControlCenterDate',
      'arrivalControlCenterDate',
      'departureControlCenterDate',
      'noticeSquadBoysDate',
      'arrivalSquadBoysDate',
      'departureSquadBoysDate',
      'noticeAmbulanceCraneAssistanceDate',
      'arrivalAmbulanceCraneAssistanceDate',
      'departureAmbulanceCraneAssistanceDate',
      'noticeRoadTeamDate',
      'arrivalRoadTeamDate',
      'departureRoadTeamDate',
      'noticeOtherDate',
      'arrivalOtherDate',
      'departureOtherDate',
      'otherName',
    ];

    const values = keys.reduce((acc, key) => {
      acc[key] =
        (defaultValues as AttentioAccidentComunication)?.[key] || undefined;
      return acc;
    }, {} as AttentioAccidentComunication);

    return values;
  };

  useEffect(() => {
    // La lista de fotos del borrador con dueño (el campo `images` de la copia
    // del paso, o lo que trajo el servidor). Es lo que se pinta y lo único que
    // el guardado sube (`imagesToSave` se calcula contra ella).
    const listedImages: Img[] = defaultValues?.images
      ? JSON.parse(defaultValues.images as string)
      : [];
    const listedNames = new Set(listedImages.map((image) => image.name));

    // 🔴 De la carpeta `accident` de OPFS, solo los ficheros que lista el
    // borrador. La carpeta es de todo el origen: la comparten las pestañas y
    // los asistentes de crear y editar accidente, y no se vacía al cambiar de
    // accidente (Ruling 5 del plan wizard-draft-ownership). Antes se cargaba
    // ENTERA, y un fichero de otro accidente con el mismo nombre que la foto
    // que se elegía la daba por repetida y no se añadía.
    const getFiles = async () => {
      let filesFromDirectory: CustomFile[] = [];
      try {
        filesFromDirectory = await getFilesFromDirectory(OrderType.Accident);
      } catch {
        // Carpeta aún sin crear: no hay ficheros locales.
        filesFromDirectory = [];
      }

      setSelectedFiles(
        filesFromDirectory.filter((file) => listedNames.has(file.id))
      );
    };

    getFiles();

    if (listedImages.length > 0) {
      setSelectedImages(listedImages);
    }
    if (defaultValues?.sketch) {
      enableSketchDebug();
      const parsedSketch = JSON.parse(defaultValues.sketch);
      log('📋 Parsed sketch from defaultValues: %O', {
        id: parsedSketch.id,
        databaseId: parsedSketch.databaseId,
        name: parsedSketch.name,
        hasDate: !!parsedSketch.date,
        hasCreatingDate: !!parsedSketch.creatingDate,
      });

      // La marca `fileName` (el nombre de su fichero, o `null` si no tiene) se
      // conserva al volver a el paso: si se perdiera, el efecto que reescribe
      // el campo dejaría el borrador sin marca y el alta lo trataría como de
      // antes del cambio (`sketchFile.ts`).
      const fileNameMarker = sketchFileNameMarker(parsedSketch);
      if (parsedSketch?.name) {
        setSketch({
          id: parsedSketch.id,
          databaseId: parsedSketch.databaseId, // T56 FIX
          name: parsedSketch.name,
          date: new Date(
            parsedSketch?.date ?? parsedSketch?.creatingDate
          ).toISOString(),
          road: parsedSketch?.road ?? defaultValues?.road ?? '',
          ...fileNameMarker,
        });
      } else {
        setSketch({
          id: parsedSketch.id,
          databaseId: parsedSketch.databaseId, // T56 FIX
          date: new Date(
            parsedSketch?.date ?? parsedSketch?.creatingDate
          ).toISOString(),
          road: parsedSketch?.road ?? defaultValues?.road ?? '',
          ...fileNameMarker,
        });
      }
    }
  }, []);

  useEffect(() => {
    const fieldsToSet = [
      {
        name: 'images',
        value: selectedImages.length > 0 ? selectedImages : null,
      },
      { name: 'sketch', value: sketch },
    ];

    fieldsToSet.forEach((field) => {
      const { name, value } = field;
      setValue(name as keyof Accident, value ? JSON.stringify(value) : null);
    });
  }, [selectedImages, sketch]);

  useEffect(() => {
    const files = selectedFiles.map((file) => file.file);
    saveFilesIntoADirectory(OrderType.Accident, files);
  }, [selectedFiles]);

  const handleAddSketch = useCallback(async () => {
    enableSketchDebug();
    const sketchDataRaw = sessionStorage.getItem(
      formAccidentNaming.IMAGE_CANVAS
    );
    log('💾 handleAddSketch - sketchDataRaw exists: %s, databaseId: %s',
      !!sketchDataRaw, sketch?.databaseId);

    if (sketchDataRaw) {
      // El croquis se guarda EN CRUDO (`AccidentSketch.onSaveDrawClick` hace
      // `setItem` directo, igual que los otros ~30 puntos que tocan estos tres
      // keys). Pasarlo por `decompressSessionStorage` era el ÚNICO sitio
      // asimétrico, y en producción destruía el dato: ese helper decide «si no
      // parsea como JSON, estaba comprimido», y un data URI de PNG no es JSON,
      // así que le aplicaba `LZString.decompressFromUTF16` y devolvía basura.
      // A partir de ahí `sketch.id` dejaba de empezar por `data:`, y al reabrir
      // el modal `openSketchModal` caía al `else` y BORRABA el croquis.
      // Solo pasaba en compilaciones de producción (en dev el helper devuelve
      // el dato tal cual), que es justo por qué no se veía probando en local.
      const sketchData = sketchDataRaw;
      const sketchBlob = dataURItoBlob(sketchDataRaw);

      const sketchFile = new File(
        [sketchBlob],
        generateFileName(
          OrderType.Accident.toUpperCase(),
          formAccidentNaming.ADD_BASIC_INFO_ACCIDENT_FORM
        ),
        {
          type: sketchBlob.type,
        }
      );

      // 🔴 El fichero de SU croquis en `canvas-accident`, con nombre LIBRE, y
      // fuera SOLO el suyo anterior, por nombre (Ruling 15; `sketchFile.ts`).
      // La carpeta la comparten las pestañas y crear y editar accidente:
      // vaciarla entera tiraba el croquis sin guardar de otra pestaña, y
      // editar subía «el primero», que podía ser el de otra. Se escribe ANTES
      // de apuntarlo en el borrador: el nombre definitivo sale de lo que haya
      // en la carpeta.
      let sketchFileName: string | null = null;
      try {
        sketchFileName = await saveDraftSketchFile(
          screenImageSource,
          directoryNaming.CANVAS,
          sketchFile,
          sketch?.fileName
        );
      } catch (error) {
        // No se pudo escribir: el croquis se ve, pero el borrador dice que no
        // tiene fichero propio y el guardado no sube ninguno (nunca otro).
        log('❌ Error saving sketch file: %O', error);
        sketchFileName = null;
      }

      const newSketch: Sketch = {
        id: sketchData,
        // T56 FIX: Preservar el ID de BD si estamos editando un sketch existente
        databaseId: sketch?.databaseId,
        date: new Date().toISOString(),
        road: defaultValues?.road ?? '',
        // 🔴 El nombre de SU fichero en `canvas-accident`: crear Y editar
        // suben ese y no «el primero de la carpeta», que comparten las
        // pestañas y crear y editar accidente (spec 2026-11-02
        // wizard-draft-ownership; revisión de la Task 7, `sketchFile.ts`).
        // `null` si no se pudo escribir: no se sube ninguno.
        fileName: sketchFileName,
      };

      log('💾 handleAddSketch - new sketch: id starts with %s, databaseId: %s',
        sketchData?.substring(0, 20), newSketch.databaseId);

      setSketch(newSketch);

      // Limpiar IMAGE_CANVAS (el blob ya está en OPFS)
      // T56 FIX: NO borrar SVG_CANVAS ni ARRAY_CANVAS - UploadImage los necesita via getSketchFromSessionStorage
      sessionStorage.removeItem(formAccidentNaming.IMAGE_CANVAS);
      log('💾 handleAddSketch - IMAGE_CANVAS cleared, SVG/ARRAY preserved for UploadImage, file saved to OPFS');
    }
    // `sketch?.fileName`: el fichero propio anterior que hay que quitar al
    // redibujar (sin él, el cierre vería el de antes del último dibujo).
  }, [sketch?.databaseId, sketch?.fileName]); // T56 FIX: Añadir databaseId a dependencias

  // T56 fix: Restore sketch data before opening modal
  const openSketchModal = async () => {
    enableSketchDebug();
    log('🖊️ openSketchModal - sketch state: %O', {
      hasSketch: !!sketch,
      sketchIdPrefix: sketch?.id?.substring(0, 30),
      isLocalDataUri: sketch?.id?.startsWith('data:'),
      databaseId: sketch?.databaseId,
      name: sketch?.name,
    });

    // T56 FIX: Prioridad de carga del sketch:
    // 1. Si sketch.id es un data URI → fue modificado localmente (no guardado en servidor aún)
    //    Usar el data URI local directamente
    // 2. Si hay databaseId → sketch existente no modificado, restaurar del servidor
    // 3. Si no hay nada → sketch nuevo, limpiar sessionStorage
    if (sketch?.id?.startsWith('data:')) {
      // El sketch fue modificado localmente pero aún no guardado en servidor
      // handleAddSketch limpió sessionStorage, restaurar desde sketch.id
      log('📍 Using local data URI (sketch modified but not saved to server yet)');
      sessionStorage.setItem(formAccidentNaming.IMAGE_CANVAS, sketch.id);
      sessionStorage.setItem(formAccidentNaming.SVG_CANVAS, '');
      sessionStorage.setItem(formAccidentNaming.ARRAY_CANVAS, '[]');
    } else if (sketch?.databaseId) {
      try {
        log('🔄 Restoring existing sketch from server: %s', sketch.databaseId);
        const restored = await restoreSketchToSessionStorage(
          sketch.databaseId,
          'accident'
        );
        if (restored) {
          log('✅ Sketch data restored successfully');
        } else {
          log('⚠️ Could not restore sketch data');
        }
      } catch (error) {
        log('❌ Error restoring sketch: %O', error);
        // Continue opening the modal even if restoration fails
      }
    } else {
      log('🧹 No sketch data found - clearing sessionStorage');
      clearSketchFromSessionStorage('accident');
    }

    dispatch(
      openModal(modalIds.ADD_SKETCH_MODAL, {
        handleAddSketch: () => {
          handleAddSketch();
          dispatch(closeModal());
        },
      })
    );
  };

  const handleChoosePick = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      setIsLoadingFiles(true);
      if (files) {
        const newImages: Img[] = [];
        const newArrayFiles: CustomFile[] = [];

        // Nombres ocupados: los de la carpeta `accident` (de este borrador o
        // de otro: otra pestaña, otro accidente, un alta a medias) y los de la
        // lista de este borrador (las del servidor no están en la carpeta).
        const takenNames = new Set<string>(
          selectedImages.map((image) => image.name)
        );
        try {
          (await listFilesWithDates(OrderType.Accident)).forEach(({ name }) =>
            takenNames.add(name)
          );
        } catch {
          // No se pudo mirar la carpeta: se sigue con la lista.
        }

        for (const file of files) {

          // El descarte de duplicados va ANTES de comprimir: al revés se
          // comprimía la foto y se tiraba el resultado (~400 ms de worker por
          // cada foto ya añadida).
          // Repetida también si entró RENOMBRADA (`foto.jpg` → `foto_2.jpg`):
          // se compara con el nombre original que guardó al entrar.
          const isDuplicate =
            selectedFiles.some((img) => img.id === file.name) ||
            wasPickedAs([...selectedFiles, ...selectedImages], file.name);
          if (isDuplicate) continue;

          // Compresión SIEMPRE (2560 px / 1,2 MB), no por umbral — ver
          // utils/imageCompression.ts.
          const compressed = await compressForUpload(file);

          // 🔴 Si el nombre ya está ocupado (un fichero de OTRO borrador en la
          // carpeta compartida, o una foto del servidor de la lista), la foto
          // entra con un nombre libre. Guardar en OPFS no pisa un fichero que
          // existe —se habría subido SU contenido— y borrar el ajeno le
          // cambiaría la foto en silencio a quien lo tenga listado (revisión de
          // la Task 7). El nombre local solo sirve para casar lista y fichero:
          // en el servidor la foto se llama por la ROID y su secuencia
          // (`planUploads`), así que renombrar no se ve.
          const id = makeUniqueFileName(file.name, takenNames);
          takenNames.add(id);
          const fileToSave =
            id === compressed.name
              ? compressed
              : new File([compressed], id, {
                  type: compressed.type,
                  lastModified: compressed.lastModified,
                });

          {
            // El nombre original, solo si entró renombrada: es lo que casa al
            // volver a elegir la misma foto (`wasPickedAs`).
            const originalName = id === file.name ? undefined : file.name;
            newImages.push({
              id,
              date: new Date(file.lastModified).toISOString(),
              name: id,
              ...(originalName && { originalName }),
            });

            newArrayFiles.push({
              id,
              file: fileToSave,
              ...(originalName && { originalName }),
            });
          }
        }

        setSelectedImages((prevImages) => [...prevImages, ...newImages]);
        setSelectedFiles((prevFiles) => [...prevFiles, ...newArrayFiles]);
      }

      setIsLoadingFiles(false);
    },
    [selectedFiles, selectedImages]
  );

  const handleRemoveImage = (id: string) => {
    setSelectedImages((currentImages) =>
      currentImages.filter((image) => image.id !== id)
    );

    setSelectedFiles((currentFiles) =>
      currentFiles.filter((file) => file.id !== id)
    );

    deleteFilesFromDirectory(OrderType.Accident, id);
  };

  const handleOpenCamera = useCallback(() => {
    dispatch(
      openModal(modalIds.OPEN_CAMERA_MODAL, {
        setImagesState: setSelectedImages,
        setFilesState: setSelectedFiles,
        dataBaseNamingCaching: formAccidentNaming.ADD_BASIC_INFO_ACCIDENT_FORM,
        type: OrderType.Accident.toUpperCase(),
      })
    );
  }, []);

  const handleAddImage = () => {
    dispatch(
      openModal(modalIds.ADD_MULTIMEDIA_MODAL, {
        handleOpenCamera: handleOpenCamera,
        handleChoosePick: () => {
          inputRef?.current?.click();
          dispatch(closeModal());
        },
      })
    );
  };

  const handleRemoveSketch = () => {
    setSketch(null);
    // Solo el fichero de ESTE borrador, por nombre (Ruling 15): el resto de
    // `canvas-accident` es de otras pestañas o del otro asistente.
    removeDraftSketchFile(screenImageSource, directoryNaming.CANVAS, sketch?.fileName);
  };

  return (
    <ContainerAttention>
      <ContainerRow>
        <Title component="h3" variant="semiBold" size="L">
          {t('accidentAttention')}
        </Title>
        <AttentionAccidentTable
          disabled={false}
          control={control}
          setValue={setValue}
          defaultValues={getAccidentTableValues()}
          errors={errors}
          accidentDate={accidentDate}
        />
      </ContainerRow>
      <SketchImagesContainer>
        <SketchContainer>
          <Typography component="h3" variant="semiBold" size="L">
            {t('accidentSketch')}
          </Typography>
          {!sketch ? (
            <MainButton
              text={t('addSketch')}
              type="button"
              onClick={openSketchModal}
            />
          ) : (
            <ContainerInputLoads>
              <InputLoadFiles
                name={'Accident'}
                {...sketch}
                onRemove={handleRemoveSketch}
              />
              <ButtonIconBackground
                variant="edit"
                type="button"
                onClick={openSketchModal}
              />
            </ContainerInputLoads>
          )}
        </SketchContainer>
        <ImagesContainer>
          <Typography component="h3" variant="semiBold" size="L">
            {t('accidentPhotos')}
          </Typography>
          {selectedImages.length === 0 ? (
            !isLoadingFiles ? (
              <MainButton
                text={t('addImages')}
                type="button"
                onClick={handleAddImage}
              />
            ) : (
              <Loader />
            )
          ) : (
            <ContainerInputLoads>
              <InputLoadImages
                imageData={selectedImages}
                removeItem={handleRemoveImage}
              />
              <ContainerRow>
                {/* Con fotos ya añadidas, esta rama NO pintaba ningún
                    indicador: al añadir más solo se bloqueaban los botones y
                    parecía que la app se había quedado colgada (feedback
                    tablet 2026-08-15). Comprimir cada foto cuesta ~400 ms de
                    worker, así que con varias la espera se nota.
                    El Loader ocupa el sitio del botón «+», que es justo donde
                    el usuario acaba de pulsar. */}
                {isLoadingFiles ? (
                  <InlineLoaderSlot>
                    <Loader />
                  </InlineLoaderSlot>
                ) : (
                  <ButtonIconBackground
                    variant="add"
                    type="button"
                    onClick={handleAddImage}
                  />
                )}
              </ContainerRow>
            </ContainerInputLoads>
          )}
          {!!errors?.images && selectedImages.length === 0 && (
            <Typography colorText="lightCoralRed" size="XS">
              {errors?.images.message}
            </Typography>
          )}
        </ImagesContainer>
        <HiddenInput
          type="file"
          accept="image/*"
          multiple
          ref={inputRef}
          onChange={handleChoosePick}
        />
      </SketchImagesContainer>
    </ContainerAttention>
  );
};
